import * as _ from "lodash";
import Api from "@/apis/Api";

const ServiceDomainsMixin = {
  data: function () {
    return {
      domainSearch: "",
      serviceDomains: [],
      domainsLoading: false,
      domainsRequestId: 0,
    };
  },
  methods: {
    getDomainName(value) {
      if (typeof value === "object" && value !== null) {
        return (value.domain || "").trim();
      }
      return (value || "").trim();
    },
    findExistingDomain(domainName) {
      const normalized = domainName.toLowerCase();
      return (
        this.serviceDomains.find(
          (item) => item.domain.toLowerCase() === normalized
        ) || null
      );
    },
    buildServiceDomainsUrl(searchTerm, serviceId) {
      const params = new URLSearchParams({ per_page: "100" });
      const term = (searchTerm || "").trim();

      if (term) {
        params.set("search_term", term);
      }

      return `/services/${serviceId}/domains?${params.toString()}`;
    },
    isDomainsResponseStale(requestId, serviceId) {
      return (
        requestId !== this.domainsRequestId ||
        serviceId !== this.selectedService?.id
      );
    },
    async loadServiceDomains(searchTerm = "") {
      if (!this.selectedService?.id) {
        return;
      }

      const serviceId = this.selectedService.id;
      const requestId = ++this.domainsRequestId;
      const url = this.buildServiceDomainsUrl(searchTerm, serviceId);
      this.domainsLoading = true;

      try {
        const response = await Api.get(url);
        if (this.isDomainsResponseStale(requestId, serviceId)) {
          return;
        }
        this.serviceDomains = response.data.data || [];
      } catch (error) {
        if (this.isDomainsResponseStale(requestId, serviceId)) {
          return;
        }
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      } finally {
        if (!this.isDomainsResponseStale(requestId, serviceId)) {
          this.domainsLoading = false;
        }
      }
    },
    cancelDebouncedDomainFetch() {
      if (this._debouncedFetchDomains && typeof this._debouncedFetchDomains.cancel === "function") {
        this._debouncedFetchDomains.cancel();
      }
    },
    scheduleDebouncedDomainFetch(searchTerm) {
      if (this._debouncedFetchDomains) {
        this._debouncedFetchDomains(searchTerm);
      }
    },
    clearServiceDomainsList() {
      this.serviceDomains = [];
    },
    resetServiceDomains() {
      this.cancelDebouncedDomainFetch();
      this.domainSearch = "";
      this.serviceDomains = [];
      this.domainsRequestId = 0;
      this.domainsLoading = false;
    },
    onSelectedServiceIdChanged() {
      this.clearServiceDomainsList();
      this.cancelDebouncedDomainFetch();
      this.domainsRequestId++;

      if (!this.isServiceDomainsSearchActive()) {
        return;
      }

      this.$nextTick(() => {
        this.loadServiceDomains(this.domainSearch || "");
      });
    },
    openServiceDomainsPicker() {
      this.cancelDebouncedDomainFetch();
      this.clearServiceDomainsList();
      this.domainsRequestId++;

      this.$nextTick(() => {
        this.loadServiceDomains("");
      });
    },
    applyDomainComboboxSelection(modelValue) {
      const domainName = this.getDomainName(modelValue);
      if (!domainName) {
        return { domainName: "", existingDomain: null, modelValue: "" };
      }

      const existingDomain = this.findExistingDomain(domainName);
      this.domainSearch = domainName;

      return {
        domainName,
        existingDomain,
        modelValue: existingDomain || domainName,
      };
    },
    syncTypedDomainFromCombobox(refName = "domainCombobox", modelValue) {
      const combobox = this.$refs[refName];
      const fromSearch = (
        combobox?.internalSearch ||
        this.domainSearch ||
        ""
      ).trim();
      const fromModel = this.getDomainName(modelValue);

      if (typeof modelValue === "object" && modelValue !== null) {
        if (
          fromSearch
          && fromSearch.toLowerCase() !== fromModel.toLowerCase()
        ) {
          this.domainSearch = fromSearch;

          return {
            domainName: fromSearch,
            existingDomain: this.findExistingDomain(fromSearch),
            modelValue: fromSearch,
          };
        }

        return this.applyDomainComboboxSelection(modelValue);
      }

      if (
        fromSearch
        && fromSearch.toLowerCase() !== fromModel.toLowerCase()
      ) {
        this.domainSearch = fromSearch;

        return {
          domainName: fromSearch,
          existingDomain: this.findExistingDomain(fromSearch),
          modelValue: fromSearch,
        };
      }

      if (fromModel) {
        return this.applyDomainComboboxSelection(modelValue);
      }

      if (!fromSearch) {
        return { domainName: "", existingDomain: null, modelValue: "" };
      }

      this.domainSearch = fromSearch;

      return {
        domainName: fromSearch,
        existingDomain: this.findExistingDomain(fromSearch),
        modelValue: fromSearch,
      };
    },
    async resolveExistingDomain(domainName) {
      const normalized = domainName.trim();
      if (!normalized) {
        return null;
      }

      let existing = this.findExistingDomain(normalized);
      if (!existing) {
        await this.loadServiceDomains(normalized);
        existing = this.findExistingDomain(normalized);
      }

      return existing;
    },
    passthroughDomainFilter() {
      return true;
    },
    isServiceDomainsSearchActive() {
      return true;
    },
    shouldSearchServiceDomainsOnInput() {
      return true;
    },
  },
  watch: {
    domainSearch: function (term) {
      if (
        !this.isServiceDomainsSearchActive()
        || !this.shouldSearchServiceDomainsOnInput()
      ) {
        return;
      }
      this.scheduleDebouncedDomainFetch(term);
    },
    "selectedService.id": function (newId, oldId) {
      if (!newId || newId === oldId) {
        return;
      }
      this.onSelectedServiceIdChanged();
    },
  },
  created() {
    this._debouncedFetchDomains = _.debounce((searchTerm) => {
      this.loadServiceDomains(searchTerm);
    }, 300);
  },
  beforeDestroy() {
    this.cancelDebouncedDomainFetch();
  },
};

export default ServiceDomainsMixin;
